/**
 * The document as plain text with positions: the plain text of each
 * textblock, blocks joined by a blank line, and for every run the document
 * position it came from. Edits match against it and review hunks map back
 * through it. No markdown or editor imports, so the highlight extension
 * (loaded by every text card) stays light.
 */
import type { Node as PMNode } from '@tiptap/pm/model';

interface Segment {
  /** Range in the index text */
  start: number;
  end: number;
  /** Document position of `start` */
  pos: number;
}

export interface TextIndex {
  text: string;
  segments: Segment[];
}

const BLOCK_JOIN = '\n\n';

/** Plain text of every textblock with the document position of each run. */
export function buildTextIndex(doc: PMNode): TextIndex {
  let text = '';
  const segments: Segment[] = [];
  let first = true;
  doc.descendants((node, pos) => {
    if (!node.isTextblock) return true;
    if (!first) text += BLOCK_JOIN;
    first = false;
    node.forEach((child, offset) => {
      const childPos = pos + 1 + offset;
      const piece = child.isText ? child.text ?? '' : child.type.name === 'hardBreak' ? '\n' : '￼';
      segments.push({ start: text.length, end: text.length + piece.length, pos: childPos });
      text += piece;
    });
    return false;
  });
  return { text, segments };
}

/** Document position for the start of a match at `offset`. */
function startPos(index: TextIndex, offset: number): number | null {
  for (const seg of index.segments) {
    if (offset < seg.end) return seg.pos + Math.max(0, offset - seg.start);
  }
  return null;
}

/** Document position for the end of a match at `offset` (exclusive). */
function endPos(index: TextIndex, offset: number): number | null {
  for (let i = index.segments.length - 1; i >= 0; i--) {
    const seg = index.segments[i];
    if (offset > seg.start) return seg.pos + Math.min(offset, seg.end) - seg.start;
  }
  return null;
}

/** A document position for an index offset (a cursor spot, e.g. where deleted text was). */
export function positionAt(index: TextIndex, offset: number): number | null {
  return startPos(index, offset) ?? endPos(index, offset);
}

/** The document range for index text [start, end). */
export function rangeForText(index: TextIndex, start: number, end: number): { from: number; to: number } | null {
  const from = startPos(index, start);
  const to = endPos(index, end);
  if (from == null || to == null || to < from) return null;
  return { from, to };
}
